import { createSelector } from "@reduxjs/toolkit";

import { choiceOutcome, filingItems, isAlreadyFiled, isFiled, isUnfiled } from "../choices/wire";
import { choiceActivity } from "../protocol/choice";
import type {
  ToolActivity,
  ToolOutcome,
  WireChoice,
  WireMessage,
  WirePart,
} from "../protocol/protocol";
import { ACTIVE_RUN, type AgentState, type OutgoingMessage, type ThreadState } from "./agentSlice";
import { liveParts, type LivePart } from "./live";
import type { TargetKey } from "./targets";

type State = { agent: AgentState };

/** What one tool call looks like right now: its result, its choice, its live state. */
export interface ToolView {
  state: "running" | ToolOutcome;
  /** The tool's line(s) for the call; null means a generic phrase. Never the tool's name. */
  activity: ToolActivity;
  result: Extract<WirePart, { kind: "tool_result" }> | null;
  choice: WireChoice | null;
  /** A chart the call showed (a `chart` part after its result), drawn instead of its line. */
  chart: ChartPart | null;
  /** The workspace page the call shows the user (the preview tool), after its result. */
  preview: PreviewPart | null;
}

export type ChartPart = Extract<WirePart, { kind: "chart" }>;

export type PreviewPart = Extract<WirePart, { kind: "preview" }>;

/**
 * The messages a view renders, in order. Each object keeps its identity while nothing it shows
 * changed, so assistant-ui (which caches conversions by source object) re-converts only what moved.
 */
export type ViewMessage =
  | {
      type: "stored";
      message: WireMessage;
      tools: Record<string, ToolView>;
      /**
       * The approvals this message's code cells asked for (`cellChoices`), by the cell's call id
       * (a sandbox_cell part's: `cellPartKey`). Their cards show after the cell: no call of the
       * model's names them (tool_call_id "").
       */
      cellChoices: Record<string, WireChoice[]>;
      /** A user message the server holds until the running answer ends (queued). */
      pending: boolean;
    }
  | {
      type: "live";
      id: string;
      parts: LivePart[];
      tools: Record<string, ToolView>;
      toolCallIds: string[];
      gap: boolean;
    }
  | { type: "outgoing"; message: OutgoingMessage };

export interface RuntimeInput {
  threadId: number | null;
  messages: readonly ViewMessage[];
  isRunning: boolean;
  hasEarlier: boolean;
}

const EMPTY: readonly ViewMessage[] = [];

const selectAgent = (state: State) => state.agent;

export const selectTarget = (state: State, key: TargetKey) => state.agent.targets[key];
export const selectTargetThread = (state: State, key: TargetKey): ThreadState | undefined => {
  const threadId = state.agent.targets[key]?.threadId;
  return threadId == null ? undefined : state.agent.threads[threadId];
};

function choiceByCall(thread: ThreadState): Map<string, WireChoice> {
  const byCall = new Map<string, WireChoice>();
  for (const choice of Object.values(thread.choices)) {
    if (choice.tool_call_id) byCall.set(choice.tool_call_id, choice);
  }
  return byCall;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** A chart the card can render: a malformed one leaves its call with its line, never a thread
 * that fails to render. */
function wellFormedChart(part: ChartPart): boolean {
  const table: unknown = part.table;
  return (
    typeof part.title === "string" &&
    isRecord(part.vegalite) &&
    isRecord(table) &&
    Array.isArray(table.columns) &&
    table.columns.every((column) => isRecord(column) && typeof column.key === "string") &&
    Array.isArray(table.rows) &&
    table.rows.every(isRecord)
  );
}

/** Each call's chart: a `chart` part belongs to the tool_result right before it. */
function chartsByCall(thread: ThreadState): Map<string, ChartPart> {
  const byCall = new Map<string, ChartPart>();
  for (const message of thread.messages) {
    let call: string | null = null;
    for (const part of message.parts) {
      if (part.kind === "tool_result") call = part.id;
      else if (part.kind === "chart" && call && !byCall.has(call) && wellFormedChart(part))
        byCall.set(call, part);
    }
  }
  return byCall;
}

function resultsByCall(thread: ThreadState) {
  const byCall = new Map<string, Extract<WirePart, { kind: "tool_result" }>>();
  // A preview follows its call's result in the same message.
  const previews = new Map<string, PreviewPart>();
  for (const message of thread.messages) {
    let call: string | null = null;
    for (const part of message.parts) {
      if (part.kind === "tool_result") byCall.set((call = part.id), part);
      else if (part.kind === "preview" && call) previews.set(call, part);
    }
  }
  return { byCall, previews };
}

/** A call's activity: the tool's lines per state from wherever they are, else its one line. */
function callActivity(...sources: (ToolActivity | undefined)[]): ToolActivity {
  const lines = sources.find(
    (source) => typeof source === "object" && source !== null && Object.keys(source).length > 0
  );
  return lines ?? sources.find((source) => typeof source === "string" && source) ?? null;
}

/** The key of a stored message's sandbox_cell part in `cellChoices`: it has no call id. */
export const cellPartKey = (index: number) => `cell:${index}`;

/** The refs a code cell asked approvals for: a sandbox_cell part's, or a repl result's card's. */
function cellApprovalRefs(cell: unknown): string[] {
  if (typeof cell !== "object" || cell === null) return [];
  const { kind, approvals } = cell as { kind?: unknown; approvals?: unknown };
  return kind === "sandbox_cell" && Array.isArray(approvals)
    ? approvals.filter((ref): ref is string => typeof ref === "string")
    : [];
}

/**
 * The one place that says which approvals a code cell asked for: the thread's choices (`all`)
 * marked with its repl call's id (`in_call`, so they show while the cell still runs or waits),
 * then those its ended cell names (`approvals`, the fallback for senders without `in_call`). Each
 * once. A cell's own approval has no call of the model's: its tool_call_id stays empty, and one
 * anchored on a call of its own shows there instead.
 */
function cellChoices(
  thread: ThreadState,
  all: WireChoice[],
  callId: string | null,
  cell: unknown
): WireChoice[] {
  const marked = callId ? all.filter((choice) => choice.in_call === callId) : [];
  const named = cellApprovalRefs(cell).flatMap((ref) => thread.choices[ref] ?? []);
  return [...new Set([...marked, ...named])].filter((choice) => !choice.tool_call_id);
}

function sameCellChoices(
  a: Record<string, WireChoice[]>,
  b: Record<string, WireChoice[]>
): boolean {
  const keys = Object.keys(a);
  return (
    keys.length === Object.keys(b).length &&
    keys.every((key) => b[key]?.length === a[key].length && a[key].every((c, i) => c === b[key][i]))
  );
}

function sameToolViews(a: Record<string, ToolView>, b: Record<string, ToolView>): boolean {
  const keys = Object.keys(a);
  return (
    keys.length === Object.keys(b).length &&
    keys.every((key) => {
      const [x, y] = [a[key], b[key]];
      return (
        y &&
        x.state === y.state &&
        x.activity === y.activity &&
        x.result === y.result &&
        x.choice === y.choice &&
        x.chart === y.chart &&
        x.preview === y.preview
      );
    })
  );
}

/**
 * One memoised builder per target: a stored message's view object is reused while its tool views
 * are unchanged, so a delta or a choice on one message re-renders that message only.
 */
function makeRuntimeInputSelector() {
  let cache = new WeakMap<WireMessage, Extract<ViewMessage, { type: "stored" }>>();
  let cachedThreadId: number | null = null;
  let live: Extract<ViewMessage, { type: "live" }> | null = null;
  const outgoing = new WeakMap<OutgoingMessage, Extract<ViewMessage, { type: "outgoing" }>>();

  const build = (thread: ThreadState | undefined, outbox: AgentState["outbox"]): RuntimeInput => {
    if (!thread) return { threadId: null, messages: EMPTY, isRunning: false, hasEarlier: false };
    if (thread.id !== cachedThreadId) {
      cache = new WeakMap();
      cachedThreadId = thread.id;
    }
    const choices = choiceByCall(thread);
    const { byCall: results, previews } = resultsByCall(thread);
    // A paused run's calls waiting for an event show their waiting line ("Waiting for Baselayer").
    const waiting = new Map((thread.run?.waiting ?? []).map((wait) => [wait.id, wait.label]));
    const charts = chartsByCall(thread);
    const toolView = (id: string, fallback: string | null): ToolView => {
      const result = results.get(id) ?? null;
      const ephemeral = thread.tools[id];
      const choice = choices.get(id) ?? null;
      return {
        state: result?.outcome ?? ephemeral?.state ?? "running",
        activity:
          (!result && waiting.get(id)) ||
          callActivity(
            ephemeral?.activity,
            result?.activity,
            choice && choiceActivity(choice),
            fallback
          ),
        result,
        choice,
        chart: charts.get(id) ?? null,
        preview: previews.get(id) ?? null,
      };
    };

    const pendingSeqs = new Set(thread.pendingSeqs);
    const views: ViewMessage[] = [];
    // A message sent during the answer is stored at once, but it is answered next: it shows after
    // the answer still streaming, not above it.
    const queued: ViewMessage[] = [];
    const storedCalls = new Set<string>();
    const allChoices = Object.values(thread.choices);
    for (const message of thread.messages) {
      if (message.role === "tool") continue; // results are shown on their calls
      const tools: Record<string, ToolView> = {};
      const cells: Record<string, WireChoice[]> = {};
      for (const [index, part] of message.parts.entries()) {
        let asked: WireChoice[] = [];
        let key = "";
        if (part.kind === "tool_call") {
          tools[part.id] = toolView(part.id, part.activity);
          storedCalls.add(part.id);
          if (part.name === "repl") {
            asked = cellChoices(thread, allChoices, part.id, results.get(part.id)?.card);
            key = part.id;
          }
        } else if (part.kind === "sandbox_cell") {
          asked = cellChoices(thread, allChoices, null, part);
          key = cellPartKey(index);
        }
        if (asked.length) cells[key] = asked;
      }
      const pending = message.role === "user" && pendingSeqs.has(message.seq);
      const previous = cache.get(message);
      const view =
        previous &&
        previous.pending === pending &&
        sameToolViews(previous.tools, tools) &&
        sameCellChoices(previous.cellChoices, cells)
          ? previous
          : { type: "stored" as const, message, tools, cellChoices: cells, pending };
      cache.set(message, view);
      (pending ? queued : views).push(view);
    }

    const isRunning = !!thread.run && ACTIVE_RUN.includes(thread.run.status);
    if (thread.live && isRunning) {
      const parts = liveParts(thread.live);
      // A call already stored shows on its message only. The runner stores each model step before
      // its tools run and announces the calls after (tool.call), and a run resumed after a choice
      // announces the deferred call again: listed here too, every line or card would show twice
      // until the next step lands.
      const toolCallIds = thread.live.toolCallIds.filter((id) => !storedCalls.has(id));
      const tools = Object.fromEntries(toolCallIds.map((id) => [id, toolView(id, null)]));
      const same =
        live &&
        live.id === `run:${thread.live.runId}:live` &&
        live.gap === thread.live.gap &&
        live.parts.length === parts.length &&
        live.parts.every((part, index) => part.text === parts[index].text) &&
        sameToolViews(live.tools, tools);
      if (!same) {
        live = {
          type: "live",
          id: `run:${thread.live.runId}:live`,
          parts,
          tools,
          toolCallIds,
          gap: thread.live.gap,
        };
      }
      views.push(live!);
    }
    views.push(...queued);

    for (const message of Object.values(outbox)) {
      if (message.threadId !== thread.id) continue;
      const view = outgoing.get(message) ?? { type: "outgoing" as const, message };
      outgoing.set(message, view);
      views.push(view);
    }
    return { threadId: thread.id, messages: views, isRunning, hasEarlier: thread.hasEarlier };
  };

  let last: RuntimeInput | null = null;
  return createSelector(
    [selectTargetThread, (state: State) => state.agent.outbox],
    (thread, outbox) => {
      const next = build(thread, outbox);
      // Keep the array's identity when every entry is the same object.
      if (
        last &&
        last.threadId === next.threadId &&
        last.isRunning === next.isRunning &&
        last.hasEarlier === next.hasEarlier &&
        last.messages.length === next.messages.length &&
        last.messages.every((view, index) => view === next.messages[index])
      ) {
        return last;
      }
      last = next;
      return next;
    }
  );
}

/** Each view calls this once (useMemo) to get its own memoised selector. */
export const makeSelectRuntimeInput = makeRuntimeInputSelector;

/** The open filing choice of a thread, for the composer popup (design 5.2). */
export const selectOpenFiling = (state: State, threadId: number | null) => {
  if (threadId == null) return null;
  const choices = state.agent.threads[threadId]?.choices ?? {};
  return (
    Object.values(choices).find(
      (choice) => choice.kind === "filing" && choice.status === "pending"
    ) ?? null
  );
};

/**
 * The filing the composer shows: the thread's open one, else the latest one confirmed or closed
 * because every file was already filed (its receipt line). Dismissed and superseded filings are
 * skipped, never shown, and so is a receipt an unfile decided after it may have made false.
 */
export const selectComposerFiling = (state: State, threadId: number | null) => {
  const open = selectOpenFiling(state, threadId);
  if (open || threadId == null) return open;
  // Filings close in the order they open (one is open at a time), so the last one is the latest.
  const choices = Object.values(state.agent.threads[threadId]?.choices ?? {});
  const last = choices.findLastIndex(
    (choice) =>
      (choice.kind === "filing" && choiceOutcome(choice) === "approved") || isAlreadyFiled(choice)
  );
  if (last < 0) return null;
  const receipt = choices[last];
  const undone = choices.some(
    (choice, index) => isUnfiled(choice) && unfiledAfter(choice, index, receipt, last)
  );
  return undone ? null : receipt;
};

/**
 * Whether the unfile was decided after the receipt, by decision time. A choice keeps the place it
 * was first seen in, so places say when each was opened: they decide only when neither has a time,
 * or only the receipt has. A receipt without one, against a timed unfile, is kept.
 */
function unfiledAfter(
  unfile: WireChoice,
  unfileIndex: number,
  receipt: WireChoice,
  receiptIndex: number
): boolean {
  const unfiledAt = Date.parse(unfile.decided_at ?? "");
  const receiptAt = Date.parse(receipt.decided_at ?? "");
  if (!Number.isNaN(unfiledAt)) return !Number.isNaN(receiptAt) && unfiledAt > receiptAt;
  return unfileIndex > receiptIndex;
}

/**
 * The thread files filed on an application, sorted: what confirmed filings did, and those that
 * already were. Compare with shallowEqual; the array is new on every call.
 */
export const selectFiledFiles = (state: State, threadId: number | null): number[] => {
  if (threadId == null) return [];
  const filed = new Set<number>();
  for (const choice of Object.values(state.agent.threads[threadId]?.choices ?? {})) {
    if (choice.kind !== "filing") continue;
    for (const item of filingItems(choice)) {
      if (isFiled(item)) filed.add(item.threadFileId);
    }
  }
  return [...filed].sort((a, b) => a - b);
};

/** A choice by its ref, wherever it is (the approval frame reads its own). */
export const selectChoice = (state: State, ref: string): WireChoice | null => {
  for (const thread of Object.values(state.agent.threads)) {
    const choice = thread.choices[ref];
    if (choice) return choice;
  }
  return null;
};

/**
 * Hold the reload while it would lose something: a focused composer with an unacknowledged
 * draft, or a run streaming into a view on screen (design 7.4).
 */
export const selectAgentBusy = createSelector([selectAgent], (agent) =>
  Object.values(agent.threads).some((thread) => {
    if (thread.composer.focused && thread.composer.dirty) return true;
    // A file put on or taken off the draft, not yet acknowledged.
    if (thread.composer.filesDirty) return true;
    if (!thread.run || thread.run.status !== "running") return false;
    return Object.values(agent.targets).some(
      (target) => target.threadId === thread.id && target.refs > 0
    );
  })
);
